import React, {useState, useEffect} from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { selectAllRoutes, selectTomorrowAllRoutes, selectAllActiveDrivers, selectAllVehicles, selectHistoryRoutes } from "../../store";
import { CustomerService, SignatureRequestService, EventsService, DriverService, EmployeeService } from "../../services";
import { buildExternalDriverNameByIdMap } from "../../shared/utils/driverDropdownOptions";
import moment from 'moment';
import { CUSTOMER_TYPE, CUSTOMER_TYPE_TEXT, PERSONAL_ROUTE_STATUS, PROGRAM_TYPE_TEXT, PAY_SOURCE_TEXT, navigateToPreviousPage } from "../../shared";
import { mergeChecklistResultWithVehicleChecklist } from "../../utils/routeChecklist";

const RouteReportWithSignature = () => {
  const params = useParams();
  const allRoutes = useSelector(selectAllRoutes);
  const tomorrowRoutes = useSelector(selectTomorrowAllRoutes);
  const historyRoutes = useSelector(selectHistoryRoutes);
  const drivers = useSelector(selectAllActiveDrivers);
  const vehicles = useSelector(selectAllVehicles);
  const [allDriverNameById, setAllDriverNameById] = useState({});
  const [externalDriverNameById, setExternalDriverNameById] = useState({});
  const currentRoute = (allRoutes.find(item => item.id === params.id)) || (tomorrowRoutes.find(item => item.id === params.id)) || (historyRoutes.find(item => item.id === params.id));
  const routeIndex = currentRoute?.type === 'inbound' ? 
    ((allRoutes.map((r) => Object.assign({}, r, {vehicleNumber: vehicles?.find((v) => r.vehicle === v.id)?.vehicle_number || 0})).sort((a, b) => a?.vehicleNumber - b?.vehicleNumber).filter(item => item.type === 'inbound').findIndex(item => item.id === params.id))) + 1 || ((tomorrowRoutes.filter(item => item.type === 'inbound').findIndex(item => item.id === params.id)))+1 || ((historyRoutes.filter(item => item.type === 'inbound').findIndex(item => item.id === params.id)))+1 :
    ((allRoutes.map((r) => Object.assign({}, r, {vehicleNumber: vehicles?.find((v) => r.vehicle === v.id)?.vehicle_number || 0})).sort((a, b) => a?.vehicleNumber - b?.vehicleNumber).filter(item => item.type === 'outbound').findIndex(item => item.id === params.id)))+1 || ((tomorrowRoutes.filter(item => item.type === 'outbound').findIndex(item => item.id === params.id))) +1 || ((historyRoutes.filter(item => item.type === 'outbound').findIndex(item => item.id === params.id)))+1;
  const currentVehicle = vehicles.find(item => item.id === currentRoute?.vehicle );
  const currentDriver = drivers.find(item => item.id === currentRoute?.driver);
  const routeDriverDisplayName = (() => {
    if (currentRoute?.driver == null || currentRoute?.driver === '') return '';
    const id = String(currentRoute.driver);
    const fromActive = drivers.find((item) => String(item.id) === id)?.name;
    if (fromActive) return fromActive;
    const fromAny = allDriverNameById[id];
    if (fromAny) return fromAny;
    const fromExternal = externalDriverNameById[id];
    if (fromExternal) return fromExternal;
    return `${currentRoute?.driver_name || ''}`.trim();
  })();
  
  const [signature, setSignature] = useState(undefined);

  const [managerSignature, setManagerSignature] = useState(undefined);
  const [customerMetaById, setCustomerMetaById] = useState(new Map());

  const site = EventsService.site;

  // Check if route name is 'by own' (case-insensitive)
  const isByOwnRoute = currentRoute?.name?.toLowerCase() === 'by own';
  const driverLabel = isByOwnRoute ? 'Staff' : 'Driver';

  const navigate = useNavigate();
  
  const getRelatedInboundOutboundRoutesForThisView = (routeType) => {
    if (allRoutes.find(item => item.id === params.id)) {
      return allRoutes.filter(item => item.type!== routeType);
    }
    if (tomorrowRoutes.find(item => item.id === params.id)) {
      return tomorrowRoutes.filter(item => item.type!==routeType);
    }
    if (historyRoutes.find(item => item.id === params.id)) {
      return historyRoutes.filter(item => item.type!==routeType);
    }
  }

  const getInboundOrOutboundRouteCustomer = (customer_id) => {
    return getRelatedInboundOutboundRoutesForThisView(currentRoute?.type)?.
      find((route) => route?.name?.toLowerCase()?.replaceAll(' ', '') === currentRoute?.name?.toLowerCase()?.replaceAll(' ', '') && route?.schedule_date == currentRoute?.schedule_date)?.route_customer_list?.
      find((cust) => cust?.customer_id === customer_id)
  }

  const getOtherRouteWithThisCustomer = (customer_id) => {
    return getRelatedInboundOutboundRoutesForThisView(currentRoute?.type)?.find((route) => route?.name !== currentRoute?.name && route?.schedule_date == currentRoute?.schedule_date && route?.route_customer_list?.find(cust => cust?.customer_id === customer_id));
  }

  const getOtherOutboundRouteWithThisCustomer = (customer_id) => {
    // console.log('what', getRelatedInboundOutboundRoutesForThisView('outbound'));
    return getRelatedInboundOutboundRoutesForThisView('inbound')?.
      find((route) => route?.schedule_date === currentRoute?.schedule_date && route?.id !== currentRoute?.id && route?.route_customer_list.find((cust) => cust?.customer_id === customer_id));
  };

  /** Inbound route (same day) where this customer appears and its name differs from the current outbound — mirrors PDF `otherRoute`. */
  const getInboundRouteDifferentNameForOutboundCustomer = (customer_id) => {
    if (currentRoute?.type !== "outbound") return null;
    const outboundNorm = normalizeRouteNameForPair(currentRoute?.name);
    return (getRelatedInboundOutboundRoutesForThisView(currentRoute?.type) || []).find(
      (r) =>
        r?.type === "inbound" &&
        r?.schedule_date === currentRoute?.schedule_date &&
        normalizeRouteNameForPair(r?.name) !== outboundNorm &&
        (r?.route_customer_list || []).some((cust) => cust?.customer_id === customer_id)
    );
  };
  
  const goBack = () => {
    navigateToPreviousPage(navigate, `/trans-routes/${params.id}`);
  }
  
  useEffect(() => {
    let isMounted = true;
    const toDateTokens = (dateValue) => {
      const tokenSet = new Set();
      const localFormatted = moment(dateValue)?.format('MM/DD/YYYY');
      const utcFormatted = moment.utc(dateValue)?.format('MM/DD/YYYY');
      [localFormatted, utcFormatted].forEach((formatted) => {
        if (!formatted || formatted.toLowerCase().includes('invalid')) return;
        const [month, day] = formatted.split('/');
        if (month && day) {
          tokenSet.add(`${month}_${day}`);
        }
      });
      return Array.from(tokenSet).map((token) => token.split('_'));
    };
    const loadSignature = async () => {
      if (!currentRoute?.id || !currentRoute?.schedule_date) {
        if (isMounted) setSignature(undefined);
        return;
      }
      const routeId = currentRoute.id;
      const requestRes = await SignatureRequestService.getAllSignatureRequests({ route_id: routeId }).catch(() => ({ data: [] }));
      const requestList = requestRes?.data || [];
      const driverIds = Array.from(new Set([
        currentRoute?.driver,
        ...requestList.map((item) => item?.driver_id)
      ].filter(Boolean)));
      const datePairs = [];
      [currentRoute?.schedule_date, ...requestList.map((item) => item?.route_date)].forEach((dateValue) => {
        toDateTokens(dateValue).forEach((pair) => datePairs.push(pair));
      });
      const uniqueDatePairs = Array.from(new Set(datePairs.map((pair) => `${pair[0]}_${pair[1]}`)))
        .map((token) => token.split('_'));
      for (const driverId of driverIds) {
        for (const [month, day] of uniqueDatePairs) {
          try {
            const data = await CustomerService.getAvatar(`${routeId}_${driverId}_${month}_${day}`);
            if (data?.data) {
              if (isMounted) setSignature(data.data);
              return;
            }
          } catch (error) {
            // Continue trying candidate keys.
          }
        }
      }
      if (isMounted) setSignature(undefined);
    };

    loadSignature();
    CustomerService.getAvatar(`route_report_manager_signature_site_${site}`)
      .then((data) => {
        if (data?.data) setManagerSignature(data.data);
        else setManagerSignature(undefined);
      })
      .catch(() => setManagerSignature(undefined));
    return () => {
      isMounted = false;
    };
  }, [currentRoute?.id, currentRoute?.driver, currentRoute?.schedule_date, site]);

  useEffect(() => {
    CustomerService.getAllCustomers()
      .then((res) => {
        const nextMap = new Map();
        (res?.data || []).forEach((customer) => {
          const meta = {
            program_type: customer?.program_type || '',
            pay_source: customer?.pay_source || ''
          };
          nextMap.set(customer?.id, meta);
        });
        setCustomerMetaById(nextMap);
      })
      .catch(() => {
        setCustomerMetaById(new Map());
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    DriverService.getAllActiveDrivers('driver')
      .then((res) => {
        if (cancelled) return;
        const next = {};
        (res?.data || []).forEach((driverItem) => {
          if (driverItem?.id == null) return;
          const name = `${driverItem?.name || ''}`.trim();
          if (!name) return;
          next[String(driverItem.id)] = name;
        });
        setAllDriverNameById(next);
      })
      .catch(() => {
        if (!cancelled) setAllDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (currentRoute?.driver == null || currentRoute?.driver === '') {
      setExternalDriverNameById({});
      return;
    }
    let cancelled = false;
    EmployeeService.getExternalUserPermissionsList(EventsService.site)
      .then((res) => {
        if (!cancelled) setExternalDriverNameById(buildExternalDriverNameByIdMap(res?.data || []));
      })
      .catch(() => {
        if (!cancelled) setExternalDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, [currentRoute?.driver]);
  const normalizeChecklistText = (value = '') =>
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const normalizedCurrentRouteName = currentRoute?.name?.toLowerCase()?.replaceAll(' ', '');
  /** Match server `normalizeRouteName` in report.controller (all whitespace removed). */
  const normalizeRouteNameForPair = (name) => (name || "").toString().toLowerCase().replace(/\s+/g, "");
  const checklistSourceRoutes = [
    currentRoute,
    ...(getRelatedInboundOutboundRoutesForThisView(currentRoute?.type) || []).filter((route) =>
      route?.schedule_date === currentRoute?.schedule_date &&
      route?.name?.toLowerCase()?.replaceAll(' ', '') === normalizedCurrentRouteName &&
      ['inbound', 'outbound'].includes(route?.type)
    )
  ].filter(Boolean);

  const vehicleById = new Map((vehicles || []).map((v) => [String(v.id), v]));
  const checklistItemsMap = new Map();
  checklistSourceRoutes.forEach((route) => {
    const v = vehicleById.get(String(route?.vehicle));
    const mergedChecklist = mergeChecklistResultWithVehicleChecklist(
      v?.checklist,
      route?.checklist_result
    );
    (mergedChecklist || []).forEach((item) => {
      const displayLabel = item?.item || item?.label || item?.key || '';
      const normalizedKey = normalizeChecklistText(displayLabel);
      if (!normalizedKey) return;
      const current = checklistItemsMap.get(normalizedKey) || {
        label: displayLabel,
        inspected: false
      };
      checklistItemsMap.set(normalizedKey, {
        label: current.label || displayLabel,
        inspected: current.inspected || item?.result === true || item?.checked === true
      });
    });
  });
  const checklistItems = Array.from(checklistItemsMap.values());
  const checklistRows = [];
  for (let i = 0; i < checklistItems.length; i += 3) {
    checklistRows.push(checklistItems.slice(i, i + 3));
  }

  const sortedRouteCustomers = [...(currentRoute?.route_customer_list || [])]
    .filter((customer) => `${customer?.customer_type || ''}`.toLowerCase() === CUSTOMER_TYPE.MEMBER)
    .sort((a, b) => {
    const aOrder = Number(a?.customer_pickup_order);
    const bOrder = Number(b?.customer_pickup_order);
    const safeA = Number.isNaN(aOrder) ? Number.MAX_SAFE_INTEGER : aOrder;
    const safeB = Number.isNaN(bOrder) ? Number.MAX_SAFE_INTEGER : bOrder;
    return safeA - safeB;
  });

  const getDisplayNameWithProgramAndPaySource = (customer) => {
    const baseName = customer?.customer_name || '';
    const customerMeta = customerMetaById.get(customer?.customer_id);
    const programTypeRaw = (
      customerMeta?.program_type ||
      customer?.customer_program_type ||
      customer?.program_type ||
      ''
    ).toString().trim();
    const paySourceRaw = (
      customerMeta?.pay_source ||
      customer?.customer_pay_source ||
      customer?.pay_source ||
      ''
    ).toString().trim();

    const qualifiers = [];
    if (programTypeRaw && programTypeRaw.toLowerCase() !== 'amdc') {
      qualifiers.push(PROGRAM_TYPE_TEXT[programTypeRaw] || programTypeRaw);
    }
    if (paySourceRaw && paySourceRaw.toLowerCase() !== 'medicaid') {
      qualifiers.push(PAY_SOURCE_TEXT[paySourceRaw] || paySourceRaw);
    }

    if (qualifiers.length === 0) return baseName;
    return `${baseName} (${qualifiers.join(', ')})`;
  };

  const getAttendanceDisplayByReportLogic = (...customerCandidates) => {
    const hasEnterCenterTime = customerCandidates.some(
      (item) => item?.customer_enter_center_time !== null && item?.customer_enter_center_time !== undefined
    );
    if (hasEnterCenterTime) {
      return { y: '✓', n: '' };
    }
    const hasInCenterStatus = customerCandidates.some(
      (item) => (item?.customer_route_status || '').toString() === PERSONAL_ROUTE_STATUS.IN_CENTER
    );
    if (hasInCenterStatus) {
      return { y: '✓', n: '' };
    }
    return { y: '', n: '✗' };
  };

  const getTransferToRouteText = (...customerCandidates) => {
    for (const customerCandidate of customerCandidates) {
      const transferToRoute = (customerCandidate?.customer_transfer_to_route || '').toString().trim();
      if (transferToRoute) return transferToRoute;
    }
    return '';
  };

  return (
    <>
      <style>
        {`
          .route-report-table th,
          .route-report-table td {
            min-width: auto !important;
            width: auto !important;
            max-width: none !important;
            border: 1px solid #333;
            padding: 4px 8px;
            font-size: 12px;
          }
          .route-report-table {
            table-layout: auto !important;
            border-collapse: collapse;
          }
          .route-report-table thead th {
            background-color: #f5f5f5 !important;
            text-align: center;
            color: #000 !important;
          }
          .route-report-header {
            border: 1px solid #333;
            padding: 8px 16px;
            margin-bottom: 8px;
            font-size: 14px;
          }
          .route-report-signature-row {
            border: 1px solid #333;
            padding: 8px 16px;
            margin-bottom: 8px;
            font-size: 14px;
          }
          .inspection-table {
            border-collapse: collapse;
            width: 100%;
            margin-top: 16px;
          }
          .inspection-table th,
          .inspection-table td {
            border: 1px solid #333;
            padding: 4px 8px;
            font-size: 11px;
          }
          .inspection-table th {
            background-color: #f5f5f5;
            color: #000;
          }
          .bilingual-label {
            display: block;
            font-size: 10px;
            color: #000 !important;
          }
        `}
      </style>
      <div className="list row noprint">
        <div className="col-md-12 text-primary mb-2">
          <button className="btn btn-link btn-sm" onClick={() => goBack()}>Back</button>
        </div>
      </div>
      
      {/* Header Row with Route Info */}
      <div className="route-report-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <strong>Route (路线):</strong> {currentRoute?.name}
        </div>
        <div style={{ flex: 1 }}>
          <strong>{driverLabel} ({isByOwnRoute ? '工作人员' : '司机'}):</strong> {routeDriverDisplayName || currentDriver?.name}
        </div>
        <div style={{ flex: 1 }}>
          <strong>Vehicle (车号):</strong> {currentVehicle?.vehicle_number}
        </div>
        <div style={{ flex: 1 }}>
          <strong>Date (日期):</strong> {currentRoute?.schedule_date && moment(currentRoute?.schedule_date).format('MM/DD/YYYY')}
        </div>
        <div style={{ background: '#eee', padding: '8px 12px', fontWeight: 'bold' }}>
          {routeIndex}
        </div>
      </div>

      {/* Signature Row */}
      <div className="route-report-signature-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <strong>{driverLabel}'s Signature ({isByOwnRoute ? '工作人员签字' : '司机签字'}):</strong>
          {signature && <img width="100px" src={`data:image/jpg;base64, ${signature}`} style={{ marginLeft: '16px' }} alt="Driver Signature" />}
        </div>
        <div style={{ flex: 1 }}>
          <strong>Manager's Signature (经理签字):</strong>
          {managerSignature ? (
            <img width="100px" src={`data:image/jpg;base64, ${managerSignature}`} style={{ marginLeft: '16px' }} alt="Manager Signature" />
          ) : (
            <img width="100px" src="/images/signature.jpeg" style={{ marginLeft: '16px' }} alt="Manager Signature default" />
          )}
        </div>
      </div>

      {/* Main Customer Table */}
      <div className="list row">
        <div className="col-md-12 mb-4">
          <table className="personnel-info-table route-report-table" style={{ tableLayout: 'auto', width: '100%' }}>
            <thead>
              {/* English Header Row */}
              <tr>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>No.<span className="bilingual-label">序号</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Name<span className="bilingual-label">姓名</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Phone<span className="bilingual-label">联系电话</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Address<span className="bilingual-label">地址</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Unit<span className="bilingual-label">房间号</span></th>
                <th colSpan={2} style={{ textAlign: 'center' }}>Attendance<span className="bilingual-label">出勤</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Pick-Up<span className="bilingual-label">接到时间</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Arrival<span className="bilingual-label">抵达中心</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Departure<span className="bilingual-label">离开中心</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Drop-Off<span className="bilingual-label">送达时间</span></th>
                <th rowSpan={2} style={{ verticalAlign: 'middle' }}>Notice<span className="bilingual-label">备注</span></th>
              </tr>
              <tr>
                <th style={{ textAlign: 'center' }}>Y</th>
                <th style={{ textAlign: 'center' }}>N</th>
              </tr>
            </thead>
            <tbody>
              {
                sortedRouteCustomers?.map((customer, index) => {
                  const relativeRouteCustomer = getInboundOrOutboundRouteCustomer(customer?.customer_id);
                  const otherRouteWithThisCustomer = getOtherRouteWithThisCustomer(customer?.customer_id);
                  const customerInOtherRoute = otherRouteWithThisCustomer?.route_customer_list?.find(cust => cust?.customer_id === customer?.customer_id);
                  const otherOutboundRoute = getOtherOutboundRouteWithThisCustomer(customer?.customer_id);
                  const switchedFromInboundRoute = getInboundRouteDifferentNameForOutboundCustomer(customer?.customer_id);
                  const attendanceDisplay = getAttendanceDisplayByReportLogic(customer, relativeRouteCustomer, customerInOtherRoute);
                  const routeTransferToRouteText = getTransferToRouteText(customer, relativeRouteCustomer, customerInOtherRoute);
                  
                  return (
                    <tr key={index}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td>{getDisplayNameWithProgramAndPaySource(customer)}</td>
                      <td>{customer?.customer_phone}</td>
                      <td>{customer?.customer_address_override || customer?.customer_address}</td>
                      <td></td>
                      <td style={{ textAlign: 'center' }}>{attendanceDisplay.y}</td>
                      <td style={{ textAlign: 'center' }}>{attendanceDisplay.n}</td>
                      <td style={{ textAlign: 'center' }}>
                        {customer?.customer_pickup_time 
                          ? new Date(customer?.customer_pickup_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                          : (relativeRouteCustomer?.customer_pickup_time 
                            ? new Date(relativeRouteCustomer?.customer_pickup_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                            : (customerInOtherRoute?.customer_pickup_time 
                              ? new Date(customerInOtherRoute?.customer_pickup_time)?.toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                              : ''))}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {customer?.customer_enter_center_time 
                          ? new Date(customer?.customer_enter_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                          : (relativeRouteCustomer?.customer_enter_center_time 
                            ? new Date(relativeRouteCustomer?.customer_enter_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                            : (customerInOtherRoute?.customer_enter_center_time 
                              ? new Date(customerInOtherRoute?.customer_enter_center_time)?.toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                              : ''))}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {customer?.customer_leave_center_time && customer?.customer_route_status !== PERSONAL_ROUTE_STATUS.SKIP_DROPOFF 
                          ? new Date(customer?.customer_leave_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                          : (relativeRouteCustomer?.customer_leave_center_time && relativeRouteCustomer?.customer_route_status !== PERSONAL_ROUTE_STATUS.SKIP_DROPOFF 
                            ? new Date(relativeRouteCustomer?.customer_leave_center_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                            : (customerInOtherRoute?.customer_leave_center_time 
                              ? new Date(customerInOtherRoute?.customer_leave_center_time)?.toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                              : ''))}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {customer?.customer_dropoff_time && customer?.customer_route_status !== PERSONAL_ROUTE_STATUS.SKIP_DROPOFF 
                          ? new Date(customer?.customer_dropoff_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                          : (relativeRouteCustomer?.customer_dropoff_time && relativeRouteCustomer?.customer_route_status !== PERSONAL_ROUTE_STATUS.SKIP_DROPOFF 
                            ? new Date(relativeRouteCustomer?.customer_dropoff_time).toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                            : (customerInOtherRoute?.customer_dropoff_time 
                              ? new Date(customerInOtherRoute?.customer_dropoff_time)?.toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'}) 
                              : ''))}
                      </td>
                      <td>
                        {routeTransferToRouteText && <div>{routeTransferToRouteText}</div>}
                        {customer?.customer_type !== CUSTOMER_TYPE.MEMBER && <div>{CUSTOMER_TYPE_TEXT[customer?.customer_type]}</div>}
                        {routeTransferToRouteText && !relativeRouteCustomer && otherRouteWithThisCustomer && (
                          <div>{`${currentRoute?.type === 'inbound' ? 'Switch to Route ' : 'Switch from Route '} ${otherRouteWithThisCustomer?.name}`}</div>
                        )}
                        {routeTransferToRouteText && customer?.customer_route_status === PERSONAL_ROUTE_STATUS.SKIP_DROPOFF && otherOutboundRoute && (
                          <div>{`Switch to Route ${otherOutboundRoute?.name}`}</div>
                        )}
                        {currentRoute?.type === "outbound" && switchedFromInboundRoute?.name && (
                          <div>{`Switched from route ${switchedFromInboundRoute.name}`}</div>
                        )}
                        {/* Rest stop check for inbound */}
                        {currentRoute?.type === 'inbound' && (() => {
                          const pickupTime = customer?.customer_pickup_time || relativeRouteCustomer?.customer_pickup_time || customerInOtherRoute?.customer_pickup_time;
                          const enterTime = customer?.customer_enter_center_time || relativeRouteCustomer?.customer_enter_center_time || customerInOtherRoute?.customer_enter_center_time;
                          if (pickupTime && enterTime) {
                            const diffMs = new Date(enterTime) - new Date(pickupTime);
                            const diffHours = diffMs / (1000 * 60 * 60);
                            if (diffHours > 1) return <div>Rest Stop</div>;
                          }
                          return null;
                        })()}
                        {/* Rest stop check for outbound */}
                        {currentRoute?.type === 'outbound' && (() => {
                          const leaveTime = customer?.customer_leave_center_time || relativeRouteCustomer?.customer_leave_center_time || customerInOtherRoute?.customer_leave_center_time;
                          const dropoffTime = customer?.customer_dropoff_time || relativeRouteCustomer?.customer_dropoff_time || customerInOtherRoute?.customer_dropoff_time;
                          if (leaveTime && dropoffTime) {
                            const diffMs = new Date(dropoffTime) - new Date(leaveTime);
                            const diffHours = diffMs / (1000 * 60 * 60);
                            if (diffHours > 1) return <div>Rest Stop</div>;
                          }
                          return null;
                        })()}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle Inspection Checklist */}
      {checklistItems.length > 0 && (
        <div className="list row">
          <div className="col-md-12">
            <table className="inspection-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Inspected</th>
                  <th>Item</th>
                  <th>Inspected</th>
                  <th>Item</th>
                  <th>Inspected</th>
                </tr>
              </thead>
              <tbody>
                {checklistRows.map((row, rowIndex) => (
                  <tr key={`checklist-row-${rowIndex}`}>
                    {[0, 1, 2].map((cellIndex) => {
                      const item = row[cellIndex];
                      if (!item) {
                        return (
                          <React.Fragment key={`checklist-empty-${rowIndex}-${cellIndex}`}>
                            <td></td>
                            <td></td>
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={`checklist-item-${rowIndex}-${cellIndex}`}>
                          <td>{item.label}</td>
                          <td style={{ textAlign: 'center' }}>{item.inspected ? '✓' : ''}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default RouteReportWithSignature;